import { CloseOutlined } from "@mui/icons-material";
import LDSUSelectModal from "components/modals/LDSUSelectModal";
import { APP_CONFIG } from "config/appConfig";
import { COLORS } from "constant";
import { useEffect, useState, useCallback } from "react";
import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { getDeviceListByGatewayId } from "service/gatewayService";
import { LDSU, WidgetItem } from "types";
import { isSensor, isActuator } from "utils/functions";
import { LDSUWidgetSensorList, LDSUDevice } from "./LDSUWidgetSensorList";
import { LDSUWidgetActuatorList } from "./LDSUWidgetActuatorList";
import { useCreateDashboardStore } from "..";
import CreateChartModal from "../modal/CreateChart";
import {
    LDSUChartResultTemplate,
    defaultLDSUWidgetConfig,
} from "./CreateTemplate";
import { GATEWAY_LABEL, LDSU_UUID_LABEL } from "constant/displayLabels";
import _ from "lodash";

type FilterDevices = {
    sensors: LDSUDevice[];
    actuators: LDSUDevice[];
};

const CreateLDSUCharts = ({ widget }: { widget: WidgetItem }) => {
    const ldsuDisplayName = APP_CONFIG.APP_IS_ONPREM
        ? "LDSU/Auxiliary Hub"
        : "LDSU";

    const [formValues, setFormValues] = useState<{ name: string }>({
        name: defaultLDSUWidgetConfig.defaultWidgetName,
    });
    const [showLDSUSelectModal, setShowLDSUSelectModal] = useState(false);
    const [selectedLDSU, setSelectedLDSU] = useState<LDSU | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    // Filter lists for sensors and actuators
    const [filterDevices, setFilterDevices] = useState<FilterDevices>({
        sensors: [],
        actuators: [],
    });

    const { createModalShow, setCreateModalShow, createData } =
        useCreateDashboardStore();

    // Fetch devices when LDSU changes
    useEffect(() => {
        const fetchDevices = async () => {
            if (!selectedLDSU?.gatewayId || !selectedLDSU?.ldsu_uuid) {
                setFilterDevices({ sensors: [], actuators: [] });
                return;
            }

            setIsLoading(true);
            try {
                const deviceList = await getDeviceListByGatewayId(
                    selectedLDSU.gatewayId
                );

                // Filter sensors and actuators by LDSU UUID
                const filteredSensors = deviceList
                    .filter(
                        (device: any) =>
                            device.ldsu_uuid === selectedLDSU.ldsu_uuid &&
                            isSensor(device.CLS)
                    )
                    .map((sensor: any, index: number) => ({
                        ldsu_uuid: sensor.ldsu_uuid,
                        SAID: sensor.SAID,
                        device_name: sensor.device_name,
                        sensor_type: sensor.sensor_type,
                        unit: sensor.unit,
                        gatewayId: sensor.gatewayId,
                        cls: sensor.CLS,
                        app: sensor.APP,
                        appId: sensor.appId || sensor.APPID,
                        color: COLORS[index % COLORS.length],
                        isActuator: false,
                    })) as LDSUDevice[];

                const filteredActuators = deviceList
                    .filter(
                        (device: any) =>
                            device.ldsu_uuid === selectedLDSU.ldsu_uuid &&
                            isActuator(device.CLS)
                    )
                    .map((actuator: any, index: number) => ({
                        ldsu_uuid: actuator.ldsu_uuid,
                        SAID: actuator.SAID,
                        device_name: actuator.device_name,
                        sensor_type: actuator.sensor_type,
                        unit: actuator.unit,
                        gatewayId: actuator.gatewayId,
                        cls: actuator.CLS,
                        app: actuator.APP,
                        appId: actuator.appId || actuator.APPID,
                        color: COLORS[
                            (filteredSensors.length + index) % COLORS.length
                        ],
                        isActuator: true,
                    })) as LDSUDevice[];

                setFilterDevices({
                    sensors: filteredSensors,
                    actuators: filteredActuators,
                });
            } catch (error) {
                console.error("Error fetching devices:", error);
                setFilterDevices({ sensors: [], actuators: [] });
            } finally {
                setIsLoading(false);
            }
        };

        fetchDevices();
    }, [selectedLDSU?.gatewayId, selectedLDSU?.ldsu_uuid]);

    // Update createData when LDSU or filter lists change
    useEffect(() => {
        if (!selectedLDSU) {
            useCreateDashboardStore.setState({
                createData: undefined,
            });
            return;
        }

        // Use actual form value so validation can catch empty name (no default fallback)
        const createDataPayload = [
            {
                chart_name: formValues.name,
                ldsu_name: selectedLDSU.name,
                gateway_name: selectedLDSU.gatewayName ?? "",
                ldsu_uuid: selectedLDSU.ldsu_uuid,
                gateway_id: selectedLDSU.gatewayId ?? "",
                deviceId: selectedLDSU.ldsu_uuid,
                // Pass sensors and actuators for transform to build attributes
                sensors: filterDevices.sensors,
                actuators: filterDevices.actuators,
            },
        ];

        useCreateDashboardStore.setState({
            createData: {
                ...LDSUChartResultTemplate,
                data: createDataPayload,
            },
        });
    }, [selectedLDSU, formValues, filterDevices]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            useCreateDashboardStore.setState({
                createData: undefined,
                createModalShow: false,
            });
        };
    }, []);

    const handleLDSUSelect = (ldsu: LDSU) => {
        setSelectedLDSU(ldsu);
        setShowLDSUSelectModal(false);
    };

    const handleRemoveLDSU = () => {
        setSelectedLDSU(null);
        setFilterDevices({ sensors: [], actuators: [] });
    };

    // Color change updates the filter list directly
    const handleColorChange = useCallback(
        (device: LDSUDevice, color: string) => {
            const key: keyof FilterDevices = device.isActuator
                ? "actuators"
                : "sensors";

            const applyColorToMatchingDevice = (d: LDSUDevice) =>
                d.SAID === device.SAID &&
                    d.ldsu_uuid === device.ldsu_uuid &&
                    d.cls === device.cls
                    ? { ...d, color }
                    : d;

            setFilterDevices((prev) => ({
                ...prev,
                [key]: prev[key].map(applyColorToMatchingDevice),
            }));
        },
        []
    );

    return (
        <>
            <Row className="mt-3">
                <Col sm="12">
                    <Form.Group>
                        <Form.Label>Widget name</Form.Label>
                        <Form.Control
                            name="name"
                            type="text"
                            placeholder={
                                defaultLDSUWidgetConfig.defaultWidgetName
                            }
                            value={formValues.name}
                            onChange={(e) =>
                                setFormValues({ name: e.target.value })
                            }
                        />
                    </Form.Group>
                </Col>
            </Row>

            <Row className="mt-3">
                <Col sm="12">
                    {!selectedLDSU && (
                        <div className="d-flex align-items-center gap-3">
                            <div>
                                <Button
                                    variant="primary"
                                    onClick={() => setShowLDSUSelectModal(true)}
                                >
                                    {`Select ${ldsuDisplayName}`}
                                </Button>
                            </div>
                        </div>
                    )}

                    {selectedLDSU && (
                        <>
                            <div className="cstm-table w-100 px-2 mt-3">
                                <Row className="d-none d-md-flex table-head px-2">
                                    <Col md={4}>{ldsuDisplayName}</Col>
                                    <Col md={4}>{GATEWAY_LABEL}</Col>
                                    <Col md={3}>{LDSU_UUID_LABEL}</Col>
                                    <Col></Col>
                                </Row>

                                <Row className="bg-secondary px-2 py-3 mb-2 align-items-center rounded w-100">
                                    <Col md={4}>{selectedLDSU.name}</Col>
                                    <Col md={4}>{selectedLDSU.gatewayName}</Col>
                                    <Col md={3}>{selectedLDSU.ldsu_uuid}</Col>
                                    <Col className="d-flex justify-content-end align-items-center">
                                        <CloseOutlined
                                            onClick={handleRemoveLDSU}
                                            className="cursor-pointer mr-2 text-primary-red-1"
                                        />
                                    </Col>
                                </Row>
                            </div>
                            <hr style={{ borderColor: "#3E4B67" }} />
                        </>
                    )}

                    {isLoading && (
                        <div className="d-flex justify-content-center py-3">
                            <Spinner animation="border" size="sm" />
                            <span className="ml-2">Loading devices...</span>
                        </div>
                    )}

                    {selectedLDSU && !isLoading && (
                        <div className="d-flex flex-column" style={{ gap: "1.5rem" }}>
                            <LDSUWidgetSensorList
                                sensors={filterDevices.sensors}
                                onColorChange={handleColorChange}
                            />
                            <LDSUWidgetActuatorList
                                actuators={filterDevices.actuators}
                                onColorChange={handleColorChange}
                            />
                        </div>
                    )}

                    <LDSUSelectModal
                        isVisible={showLDSUSelectModal}
                        onClose={() => setShowLDSUSelectModal(false)}
                        onFinish={handleLDSUSelect}
                        defaultSearchValue={{
                            gatewayId: "",
                            search: "",
                        }}
                        unitType="LDSU"
                        selectedLDSU={selectedLDSU}
                    />
                </Col>
            </Row>

            <CreateChartModal
                show={createModalShow && !_.isEmpty(createData)}
                onHide={() => setCreateModalShow(false)}
            />
        </>
    );
};

export default CreateLDSUCharts;
